import { useCallback, useMemo, useState } from "react";
import { cancelSubscription } from "../../utils/APIs";
import { useUserContext } from "../../contexts/UserContext";
import TransactionHistory from "../../components/Stripe/TransactionHistory";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils/converter";
import PaymentMethods from "./PaymentMethods";
import { formatPlanDuration } from "../../utils/billing";
import ConfirmModal from "../UI/Modal/confirmModal";

const EmbeddedBilling = ({ subscription, onRefresh }) => {
    const { userData } = useUserContext();
    const [loading, setLoading] = useState(false);
    const [openConfirmCancel, setOpenConfirmCancel] = useState(false);

    const orgId = userData?.selectedOrganization?.org_token;
    const token = userData?.token;

    /* -------------------- Derived Values -------------------- */
    const isActive = useMemo(
        () =>
            subscription?.status === "active" ||
            subscription?.status === "trial",
        [subscription?.status]
    );

    const statusBadgeStyles = useMemo(() => {
        return isActive
            ? "bg-emerald-100 text-emerald-700"
            : "bg-rose-100 text-rose-700";
    }, [isActive]);

    /* -------------------- Handlers -------------------- */
    const handleCancelSubscription = useCallback(async () => {
        if (!orgId || !token) return;

        try {
            setLoading(true);

            const res = await cancelSubscription(token, orgId);

            if (res?.status) {
                toast.success("Subscription cancelled successfully.");
                if (onRefresh) onRefresh();
            } else {
                toast.error("Failed to cancel subscription.");
            }
        } catch (error) {
            toast.error(error?.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    }, [orgId, token]);

    console.log(subscription, "subscription");

    /* -------------------- Render -------------------- */
    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            {openConfirmCancel && <ConfirmModal
                title={"Cancel Subscription"}
                description={"Are you sure you want to cancel your subscription? Your access will remain until the end of the current billing period."}
                hideModal={() => setOpenConfirmCancel(false)}
                ConfirmText={"Cancel Subscription"}
                CancelText={"Cancel"}
                handleConfirmation={handleCancelSubscription}
            />}

            <div className="max-w-6xl mx-auto space-y-10">
                {/* Header */}
                <header>
                    <h1 className="text-3xl font-bold text-gray-900">
                        Billing & Subscription
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Manage your plan, payment methods and invoices.
                    </p>
                </header>

                {/* Current Plan Card */}
                <section className="bg-white rounded-2xl shadow-sm border p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                                Current Plan
                            </p>

                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-semibold text-gray-900">
                                    {subscription?.plan_id?.plan_name || "No Plan"}
                                </h2>

                                {subscription?.status && (
                                    <span
                                        className={`px-3 py-1 text-xs font-semibold rounded-full ${statusBadgeStyles}`}
                                    >
                                        {subscription.status.toUpperCase()}
                                    </span>
                                )}
                            </div>

                            {subscription && (
                                <p className="text-gray-500 mt-2 text-sm">
                                    {subscription?.plan_id?.price}{" "}
                                    {subscription?.plan_id?.price_currency?.toUpperCase()} /
                                    {" "}
                                    {formatPlanDuration(subscription?.plan_id?.duration)}
                                    {" • "}
                                    Next billing:{" "}
                                    {subscription?.end_date
                                        ? formatDate(subscription.end_date)
                                        : "N/A"}
                                </p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Link
                                to="/pricing"
                                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition"
                            >
                                Upgrade Plan
                            </Link>

                            {subscription && isActive && (
                                <button
                                    onClick={() => setOpenConfirmCancel(true)}
                                    disabled={loading}
                                    className="px-5 py-2.5 border border-rose-200 text-rose-600 rounded-xl text-sm font-medium hover:bg-rose-50 transition disabled:opacity-50"
                                >
                                    {loading ? "Cancelling..." : "Cancel Subscription"}
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                {/* Payment Methods */}
                <section className="bg-white rounded-2xl shadow-sm border p-6">
                    <PaymentMethods />
                </section>

                {/* Billing History */}
                <section className="bg-white rounded-2xl shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Billing History
                    </h2>
                    <TransactionHistory />
                </section>
            </div>
        </div>
    );
};

export default EmbeddedBilling;
